import type { Immutable } from "immer";
import React from "react";

import type { HardcodedBlock as HardcodedBlockValue } from "@reactivated";

import { CeoMessage } from "./versions/CeoMessage";
import { CertipurUs } from "./versions/CertipurUs";
import { CompareAdapt } from "./versions/CompareAdapt";
import { CompareAdapt2 } from "./versions/CompareAdapt2";
import { CompareBreeze } from "./versions/CompareBreeze";
import { CompareBreeze2 } from "./versions/CompareBreeze2";
import { GrayTestimonials } from "./versions/GrayTestimonials";
import {
    JdPower2022BannerDouble,
    JdPower2022BannerDoubleV2,
    JdPower2022BannerMultiple,
} from "./versions/JdPower2022Banners";
import {
    JdPower2022BlockDark,
    JdPower2022BlockInStoreDark,
    JdPower2022BlockInStoreLight,
    JdPower2022BlockLight,
} from "./versions/JdPower2022Blocks";
import { JdPower2023BannerTriple } from "./versions/JdPower2023";
import {
    JdPower2024BannerCenteredWithYears,
    JdPower2024BannerFirstVersion,
    JdPower2024BannerQuadruple,
} from "./versions/JdPower2024";
import {
    JdPower2026BannerBlueMobile,
    JdPower2026BannerOverlay,
} from "./versions/JdPower2026";
import { OrientTestimonials } from "./versions/OrientTestimonials";
import { SocialBanner } from "./versions/SocialBanner";

import ceoMessageStyles from "./versions/CeoMessage.module.scss";
import certipurUsStyles from "./versions/CertipurUs.module.scss";
import compareAdapt2Styles from "./versions/CompareAdapt2.module.scss";
import compareAdaptStyles from "./versions/CompareAdapt.module.scss";
import compareBreeze2Styles from "./versions/CompareBreeze2.module.scss";
import compareBreezeStyles from "./versions/CompareBreeze.module.scss";
import orientTestimonialsStyles from "./versions/OrientTestimonials.module.scss";
import socialBannerStyles from "./versions/SocialBanner.module.scss";

/**
 * Per-version React component registry for ``HardcodedBlockTemplate`` (#33404).
 *
 * Each entry is keyed by the full ``hc-block--<name>`` version string (the raw
 * ``version`` field value). The dispatcher in ``HardcodedBlockTemplate`` looks
 * the version up here: a hit renders the native React component inside the
 * ``<section class="hc-block ...">`` wrapper; a miss renders ``null`` (matching
 * the legacy ``{% file_exists %}`` else-branch, which emitted only an HTML
 * comment). ALL non-prequal versions now render natively here -- there is no
 * server-rendered ``html`` fallback anymore (#33404 part 2). The
 * ``hc-block--prequal-card`` interactive island stays a dedicated branch in the
 * dispatcher and is deliberately NOT registered here.
 *
 * Each component returns the partial's inner markup as a fragment (the wrapper
 * ``<section>`` is supplied by the dispatcher), reproducing the legacy Django
 * partial DOM structure. Components get the block ``value``; only versions
 * with CMS-editable bits (the JD Power 2026 "Learn More" link) read it.
 * Versions migrated to CSS modules replace their legacy
 * classnames with module classes; the rest still rely on the global
 * ``hc-block--*`` stylesheets. ``rootClassName`` is
 * the version's module root class; the dispatcher adds it to the ``<section>``
 * beside the literal version value, which stays as the CMS value. Versions
 * without one render no module class.
 */
type IVersionEntry = Readonly<{
    Component: (
        props: Immutable<{ value: HardcodedBlockValue }>,
    ) => React.JSX.Element;
    rootClassName?: string;
}>;

export const VERSIONS: Readonly<Partial<Record<string, IVersionEntry>>> = {
    "hc-block--certipur-us": {
        Component: CertipurUs,
        rootClassName: certipurUsStyles.certipurUs,
    },
    "hc-block--ceo-message": {
        Component: CeoMessage,
        rootClassName: ceoMessageStyles.ceoMessage,
    },
    "hc-block--compare-adapt": {
        Component: CompareAdapt,
        rootClassName: compareAdaptStyles.compareAdapt,
    },
    "hc-block--compare-adapt-2": {
        Component: CompareAdapt2,
        rootClassName: compareAdapt2Styles.compareAdapt2,
    },
    "hc-block--compare-breeze": {
        Component: CompareBreeze,
        rootClassName: compareBreezeStyles.compareBreeze,
    },
    "hc-block--compare-breeze-2": {
        Component: CompareBreeze2,
        rootClassName: compareBreeze2Styles.compareBreeze2,
    },
    "hc-block--social-banner": {
        Component: SocialBanner,
        rootClassName: socialBannerStyles.socialBanner,
    },
    "hc-block--gray-testimonials": { Component: GrayTestimonials },
    "hc-block--orient-testimonials": {
        Component: OrientTestimonials,
        rootClassName: orientTestimonialsStyles.orientTestimonials,
    },
    "hc-block--jdpower-2023--banner-triple": {
        Component: JdPower2023BannerTriple,
    },
    "hc-block--jdpower-2024--banner-centered-with-years": {
        Component: JdPower2024BannerCenteredWithYears,
    },
    "hc-block--jdpower-2024--banner-first-version": {
        Component: JdPower2024BannerFirstVersion,
    },
    "hc-block--jdpower-2024--banner-quadruple": {
        Component: JdPower2024BannerQuadruple,
    },
    "hc-block--jdpower-2026--banner-overlay": {
        Component: JdPower2026BannerOverlay,
    },
    "hc-block--jdpower-2026--banner-blue-mobile": {
        Component: JdPower2026BannerBlueMobile,
    },
    "hc-block--jdpower-2022--banner-double-v2": {
        Component: JdPower2022BannerDoubleV2,
    },
    "hc-block--jdpower-2022--banner-double": {
        Component: JdPower2022BannerDouble,
    },
    "hc-block--jdpower-2022--banner-multiple": {
        Component: JdPower2022BannerMultiple,
    },
    "hc-block--jdpower-2022--block-dark": { Component: JdPower2022BlockDark },
    "hc-block--jdpower-2022--block-in-store-dark": {
        Component: JdPower2022BlockInStoreDark,
    },
    "hc-block--jdpower-2022--block-in-store-light": {
        Component: JdPower2022BlockInStoreLight,
    },
    "hc-block--jdpower-2022--block-light": { Component: JdPower2022BlockLight },
};
